import {
  useMonitoringActive,
  useFollowedAccounts,
} from '@/modules/engagement/hooks';

import type { ReactNode } from 'react';

type Props = { collapsed?: boolean };

const borderStyle = { borderColor: 'var(--border-subtle)' };
const textStyle = { color: 'var(--text-muted)' };

function useServiceCount(): { count: number; isLoading: boolean } {
  const { isActive, isLoading: monitoringLoading } = useMonitoringActive();
  const { data: accounts, isLoading: accountsLoading } = useFollowedAccounts();
  const activeAccounts = accounts?.filter((a) => a.is_active).length ?? 0;
  const count = isActive && activeAccounts > 0 ? 1 : 0;
  return { count, isLoading: monitoringLoading || accountsLoading };
}

function CollapsedView({ count }: { count: number }): ReactNode {
  const dotCls = count > 0 ? 'bg-success' : 'bg-muted';
  return (
    <div className="flex justify-center">
      <span className={`w-2 h-2 rounded-full ${dotCls}`} />
    </div>
  );
}

function ExpandedView({ count }: { count: number }): ReactNode {
  const dotCls = count > 0 ? 'bg-success' : 'bg-muted';
  const label = count === 1 ? '1 service running' : `${count} services running`;

  return (
    <div className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full ${dotCls}`} />
      <span style={textStyle} className="text-xs">
        {label}
      </span>
    </div>
  );
}

export function ServicesCounter({ collapsed }: Props): ReactNode {
  const { count } = useServiceCount();

  return (
    <div style={borderStyle} className="p-3 border-t">
      {collapsed ? (
        <CollapsedView count={count} />
      ) : (
        <ExpandedView count={count} />
      )}
    </div>
  );
}
